import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Briefcase, CheckSquare, AlertCircle } from '../../icons';
import { searchProjectHome, type ProjectSearchResultDto } from '../../api/project-home';
import { DetailPageHeader } from './DetailPageHeader';
import { ProjectSearchBox } from './ProjectSearchBox';
import { projectSearchTarget } from './project-nav-targets';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectPage.css';

/**
 * ⭐ **프로젝트 통합 검색 결과 (`/project/search?q=`)** — 2026-10-07 사용자 결정 **㉠-2**.
 *
 * ## 무엇을 고치나
 *
 * ```
 * 서버    GET /api/v1/project-home/search  (2026-08-25 · 시험 10건)
 * 프론트  ⛔ 부르는 곳 «0건»  ⇒ 홈 검색창은 ★ 이미 받아 둔 것을 걸렀다
 *   ⇒ ⇒ 찾는 태스크가 /tasks/me 상위 20건(TODO·진행·검토) 밖이면 ⛔ 걸리지도 않았다
 *   ⇒ ⇒ 담당자는 memberPreview 상위 5명까지만 · 완료(DONE) 태스크는 ★ 아예 0건
 * ```
 * ⇒ ★ 이 화면이 **서버에 직접 묻는다.**
 *
 * ## ★ 본으로 삼은 것 — ⚠ 「하나」가 아니라 ⭐ 둘이다 (재서 골랐다)
 *
 * ```
 * 파일                 useSearchParams   <section   <h2
 * ProjectsDetailPage        ★ 2            0         0     ⇐ 껍데기 · ?q= 딥링크
 * StatsDetailPage             0          ★ 5       ★ 4     ⇐ 묶음 쌓기 · 건수 · 빈 상태
 * MyTasksDetailPage           0            1         0
 * TeamsDetailPage             0            2         1
 * ```
 * ⇒ `?q=` 를 **주소에서** 읽는 꼴은 `ProjectsDetailPage` 의 `?team=` 과 같다(넷 중 그 파일만 있다).
 * ⇒ 묶음을 쌓고 각자 제목·건수·빈 상태를 다는 꼴은 `StatsDetailPage` 에 **이미 있다**.
 *
 * ## ⭐ 디바운스가 ★ 없다 — 「누르면 간다」가 그 자리를 ⭐ 없앤다
 *
 * 서버 라우트 머리말: 「audit 은 남기지 않는다. **타이핑마다 도는** read-only 조회라 …」.
 * ⇒ ★ 그 전제는 **글자마다 부를 때만** 생긴다. ⇒ 이 화면은 **「찾기」를 눌러야** 부른다.
 * ⇒ ⇒ ⭐ 그래서 **「적어도 안 부른다」가 시험으로 잠긴다** — 「몇 ms 뒤」는 사보타주로 안 갈린다.
 * ⚠ 그리고 **주소가 진실이다** — 적는 글자는 제 상태(`draft`)이고, 조회는 `?q=` 가 바뀔 때만 돈다.
 *   ⇒ 그래서 뒤로 가기·새로 고침·링크 공유가 **같은 결과**를 낸다.
 *
 * ## ⛔ 「0건」과 「안 물었다」를 ★ 가른다
 *
 * ```
 * q 가 비었다  ⇒ ⭐ 묶음을 «안 그린다»(서버도 안 부른다) — ⛔ 「해당 없음」이라고 말하면 거짓이다
 * q 가 있는데 결과 0 ⇒ ★ 묶음마다 「해당 없음」
 * ```
 */

const LIMIT = 20;

/**
 * ⭐ **「더 있습니다」** — 그 묶음이 ★ 상한에서 «잘렸을» 때만 그린다 (2026-10-08).
 *
 * ## ⛔ N 을 ★ 적을 수 «없다»
 *
 * ```
 * 서버가 주는 것 ⇒ ★ hasMore «하나»뿐 (take: limit+1 로 «넘치나»만 본다 — 새 질의 0)
 * ⛔ total 은 ⇒ ★ 없다 (그 라우트 코드에 total 0건 — 주석에만 「왜 안 주나」가 있다)
 * ⇒ ⇒ ★ 그래서 ⭐ 「N건 더 있음」이 ⛔ 불가능하다. ⇒ 「더 있습니다」로만 말한다.
 * ```
 * ⚠ N 이 필요해지면 ⇒ ★ 서버에 **count 질의 셋**을 더 요청해야 한다(비용이 는다).
 *
 * ## ⚠ 「거슬리지 않게」 — ★ 상한에 ⭐ «자주» 닿는다
 *
 * 화면이 보내는 `LIMIT` 이 **서버 상한(20)과 같은 값**이라 ⇒ ★ 20건에 닿는 질의가 흔하다.
 * ⇒ ⇒ ⭐ 그래서 **흐린 작은 글자**로 두고(`pj-empty` 와 같은 결) ★ 단추로 만들지 않았다 —
 *   ⛔ 눌러도 **더 볼 길이 없다**(그 끝점에 offset 이 없다). ⇒ ★ 누를 수 있게 보이면 거짓말이다.
 * ⇒ ★ 대신 ⭐ «할 수 있는 일»을 말한다: 「검색어를 좁혀 보세요」.
 */
function MoreNote() {
  return (
    <div className="pj-empty pj-search-more" role="status">
      {tProject('project.search.has_more')}
    </div>
  );
}

export function ProjectSearchPage() {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') ?? '').trim();

  // ⚠ 적는 글자는 «여기»에 있고, 조회는 주소(q)가 바뀔 때만 돈다 — 둘을 섞으면 타이핑마다 부른다.
  const [draft, setDraft] = useState(q);
  const [result, setResult] = useState<ProjectSearchResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 주소로 들어오거나 뒤로 갔을 때 입력칸을 맞춘다.
  useEffect(() => { setDraft(q); }, [q]);

  const load = useCallback(async () => {
    if (q.length === 0) { setResult(null); setError(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      setResult(await searchProjectHome(q, LIMIT));
    } catch (err) {
      // ⛔ 빈 묶음으로 메우지 않는다 — 그러면 화면이 「해당 없음」이라고 거짓말한다.
      // 말은 StatsDetailPage 와 같은 꼴로 집는다(서버 말 ▸ 오류 말 ▸ 우리 말).
      setResult(null);
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message ?? tProject('project.detail.stats.err'));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { void load(); }, [load]);

  const openTask = useCallback((projectId: string, teamId: string | null) => {
    // 3c 홈·위젯과 같은 꼴 — 팀 스코프까지 실어 보낸다(design D-5).
    navigate(teamId ? `/task?team=${teamId}&project=${projectId}` : `/task?project=${projectId}`);
  }, [navigate]);

  return (
    <div className="pj-page">
      <DetailPageHeader title={tProject('project.search.title')} />

      <ProjectSearchBox
        value={draft}
        onChange={setDraft}
        onSubmit={(next) => navigate(projectSearchTarget(next))}
      />

      {error && (
        <div className="pj-error" role="alert">
          <AlertCircle size={14} aria-hidden="true" /> {error}
          <button type="button" className="pj-button" onClick={() => void load()}>
            {tProject('project.btn.retry')}
          </button>
        </div>
      )}

      {loading && !error && <div className="pj-loading" role="status">…</div>}

      {/* ★ q 가 비면 여기 아래를 아예 안 그린다 — 「해당 없음」은 «물어봤을 때»만 할 수 있는 말이다. */}
      {q.length > 0 && !loading && !error && result && (
        <div className="pj-stats-detail-lists">
          <section className="pj-stats-detail-card" aria-label={tProject('project.search.spaces')}>
            <h2 className="pj-section-heading">
              <Users size={15} aria-hidden="true" />
              {tProject('project.search.spaces')} ({result.spaces.length}{result.spacesHasMore ? '+' : ''})
            </h2>
            {result.spaces.length === 0
              ? <div className="pj-empty" role="status">{tProject('project.detail.stats.empty_list')}</div>
              : (
                <ul className="pj-mytask-items">
                  {result.spaces.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className="pj-mytask-row"
                        onClick={() => navigate(`/project/teams?team=${s.id}`)}
                      >
                        <span className="pj-mytask-main">
                          <span className="pj-mytask-title">{s.name}</span>
                          <span className="pj-mytask-meta">
                            {tProject('project.home.team_meta', {
                              m: String(s.memberCount), p: String(s.projectCount),
                            })}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            {result.spacesHasMore && <MoreNote />}
          </section>

          <section className="pj-stats-detail-card" aria-label={tProject('project.detail.projects.title')}>
            <h2 className="pj-section-heading">
              <Briefcase size={15} aria-hidden="true" />
              {tProject('project.detail.projects.title')} ({result.projects.length}{result.projectsHasMore ? '+' : ''})
            </h2>
            {result.projects.length === 0
              ? <div className="pj-empty" role="status">{tProject('project.detail.stats.empty_list')}</div>
              : (
                <ul className="pj-mytask-items">
                  {result.projects.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="pj-mytask-row"
                        onClick={() => openTask(p.id, p.teamId)}
                      >
                        <span className="pj-mytask-main">
                          <span className="pj-mytask-title">{p.name}</span>
                          {/* ⚠ teamName 은 soft-delete 된 팀이면 null 이다(서버 규칙) — 그때는 안 적는다. */}
                          {p.teamName && <span className="pj-mytask-meta">{p.teamName}</span>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            {result.projectsHasMore && <MoreNote />}
          </section>

          <section className="pj-stats-detail-card" aria-label={tProject('project.search.tasks')}>
            <h2 className="pj-section-heading">
              <CheckSquare size={15} aria-hidden="true" />
              {tProject('project.search.tasks')} ({result.tasks.length}{result.tasksHasMore ? '+' : ''})
            </h2>
            {result.tasks.length === 0
              ? <div className="pj-empty" role="status">{tProject('project.detail.stats.empty_list')}</div>
              : (
                <ul className="pj-mytask-items">
                  {result.tasks.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        className="pj-mytask-row"
                        onClick={() => openTask(t.project.id, t.project.teamId)}
                      >
                        <span className="pj-mytask-main">
                          <span className="pj-mytask-title">{t.title}</span>
                          <span className="pj-mytask-meta">
                            {t.project.teamName ? `${t.project.teamName} · ${t.project.name}` : t.project.name}
                          </span>
                        </span>
                        <span className="pj-mytask-side">
                          {/* 상태·우선순위 글귀는 기존 열쇠를 그대로 쓴다(같은 뜻을 다른 글자로 두지 않는다). */}
                          {tProject(`project.status.${t.status}` as 'project.status.TODO')}
                          {t.dueDate ? ` · ${t.dueDate}` : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            {result.tasksHasMore && <MoreNote />}
          </section>
        </div>
      )}
    </div>
  );
}
